import { useState } from 'react'

import { Card, CardContent, CardHeading } from '@/components/ui'
import {
  AddCaseFactButton,
  CaseDetailsGrid,
  CaseFactList,
} from '@/modules/projects/components/caseFacts'
import { useCaseFacts, useProject } from '@/modules/projects/hooks'

interface OverviewCaseFactsCardProps {
  slug: string
}

// The scrollbar sits in the card's own padding, so the row icons stay under the Add button
// whether or not the list scrolls.
const FACT_ROW_REM = 2.5625
const MAX_ROWS_KEPT_IN_VIEW = 5

const FACT_SCROLL_AREA =
  '-mr-4 max-h-72 pr-1.5 [scrollbar-gutter:stable] @4xl:max-h-none @4xl:min-h-0 @4xl:flex-1'

// Two columns once the card is wide: the case setup answers, then the free-form facts.
// One line, only between the two columns. The answers set the card's height and the facts
// scroll inside it, so a long list never stretches the card.
export const OverviewCaseFactsCard = ({ slug }: OverviewCaseFactsCardProps) => {
  const project = useProject()
  const [isAdding, setIsAdding] = useState(false)
  const rowCount = useCaseFacts().length + (isAdding ? 1 : 0)
  // The case answers set the card height. This keeps room for a few facts when they are short.
  const roomForRows = Math.min(Math.max(rowCount, 1), MAX_ROWS_KEPT_IN_VIEW)

  if (!project?.isAiFeaturesEnabled) return null

  return (
    <Card>
      <CardHeading
        title="Case facts"
        actions={
          <AddCaseFactButton
            isAdding={isAdding}
            onClick={() => setIsAdding(true)}
          />
        }
      />
      <CardContent className="@container">
        <div className="grid gap-8 @4xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] @4xl:gap-0 @4xl:divide-x">
          <CaseDetailsGrid className="@4xl:pr-8" />
          <section
            className="relative"
            style={{ minHeight: `${roomForRows * FACT_ROW_REM}rem` }}
          >
            <div className="flex flex-col gap-3 @4xl:absolute @4xl:inset-y-0 @4xl:right-0 @4xl:left-8">
              <CaseFactList
                slug={slug}
                isAdding={isAdding}
                onAddingDone={() => setIsAdding(false)}
                scrollClassName={FACT_SCROLL_AREA}
              />
            </div>
          </section>
        </div>
      </CardContent>
    </Card>
  )
}
